import type { BaseDocument } from '../mongo/base-document.interface';

/** `mtd` covers the 1st of the current month through today (UTC), inclusive. */
export const KPI_GRANULARITY = { DAY: 'day', MONTH: 'month', MONTH_TO_DATE: 'mtd' } as const;
export type KpiGranularity = (typeof KPI_GRANULARITY)[keyof typeof KPI_GRANULARITY];

/** Shared frontend/backend heartbeat cadence for an actively-viewed `day` KPI — single source of truth. */
export const KPI_ACTIVE_HEARTBEAT_MS = 60_000;

/** One entry per KPI pipeline defined in the `pharos-back-deliveries` KPI registry (code, not database). */
export const KPI_KEYS = { DRIVER_TASK_KPIS: 'driver-task-kpis', ORDER_KPIS: 'order-kpis' } as const;
export type KpiKey = (typeof KPI_KEYS)[keyof typeof KPI_KEYS];

export function isKpiKey(value: string): value is KpiKey {
  return (Object.values(KPI_KEYS) as string[]).includes(value);
}

/** A UTC calendar bound for a KPI period. `day` is optional because callers may construct a bound before it is fully resolved. */
export interface KpiPeriodBound {
  day?: number;
  month: number;
  year: number;
}

/**
 * A precomputed KPI document. `spaceId`/`siteId` are both absent for a tenant-level KPI, and the
 * pair (`key`, `granularity`, `from`, `spaceId`, `siteId`) identifies the document uniquely within
 * a tenant. `metrics`/`computedAt` are absent until the first successful pipeline run.
 */
export interface Kpi extends BaseDocument {
  computedAt?: Date;
  from: KpiPeriodBound;
  granularity: KpiGranularity;
  key: KpiKey;
  /** Last time this KPI was requested by a client — drives cron refresh eligibility (unused KPIs are never refreshed). */
  lastUsed: Date;
  metrics?: KpiMetrics;
  siteId?: string;
  spaceId?: string;
  to: KpiPeriodBound;
}

export type CreateKpi = Omit<Kpi, 'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'>;

/**
 * Narrow metrics shape for the `order-kpis` KPI key. `total` is always the sum of `anomaly`,
 * `unassigned`, `inprogress`, `completed`, and `failed` (a cancelled order is excluded from every
 * group, including `total`). `completed` is always the sum of `completedOnTime` and
 * `completedLate` — an order completed at or before `requestedTimeWindow.end` counts as on time,
 * strictly after counts as late, and a legacy order missing `completedAt` counts as on time.
 */
export interface OrderKpiMetrics {
  anomaly: number;
  completed: number;
  completedLate: number;
  completedOnTime: number;
  failed: number;
  inprogress: number;
  total: number;
  unassigned: number;
}

/**
 * One row of the `driver-task-kpis` KPI key — per-driver task counts for a period. Deliberately
 * additive-friendly: later releases add alert columns to this same row shape.
 */
export interface DriverTaskKpiRow {
  assigned: number;
  driverId: string;
  driverName: string;
  failed: number;
  inprogress: number;
  late: number;
  ontime: number;
  pickupSiteNames: string[];
}

/**
 * The stored shape of `Kpi.metrics` — a single merged record for `$group: { _id: null }` pipelines
 * (`order-kpis`), or the full list of per-group rows for row-shaped pipelines (`driver-task-kpis`).
 */
export type KpiMetrics = DriverTaskKpiRow[] | Record<string, number>;

/** Narrows `Kpi.metrics` to the `driver-task-kpis` row-array shape. */
export function isDriverTaskKpiRows(metrics: KpiMetrics | undefined): metrics is DriverTaskKpiRow[] {
  return Array.isArray(metrics);
}
